import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { setApiToken } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]                 = useState(null);
  const [terminalUser, setTerminalUser] = useState(null);
  const [terminalToken, setTerminalToken] = useState(null); // token konta Terminal
  const [workerToken, setWorkerToken]   = useState(null);   // token zalogowanego pracownika
  const [loading, setLoading]           = useState(true);
  const [timeoutSecs, setTimeoutSecs]   = useState(120);
  const timerRef                        = useRef(null);

  useEffect(() => {
    const token  = localStorage.getItem('cafepos_token');
    const stored = localStorage.getItem('cafepos_user');
    if (token && stored) {
      try {
        const u = JSON.parse(stored);
        setUser(u);
        if (u.role === 'terminal') {
          setTerminalToken(token);
          fetch('/api/settings', { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.json())
            .then(s => { if (s.terminal_timeout) setTimeoutSecs(parseInt(s.terminal_timeout)); })
            .catch(() => {});
        }
      } catch {}
    }
    setLoading(false);
  }, []);

  // Globalne przechwytywanie fetch - jeśli token wygasł/jest nieprawidłowy (backend
  // zwraca 401), wyloguj i przekieruj na ekran logowania. Robimy to raz, na poziomie
  // window.fetch, żeby nie trzeba było tego łatać w dziesiątkach pojedynczych zapytań
  // rozsianych po całej aplikacji. Twarde przekierowanie (window.location.href), bo
  // AuthProvider jest poza <BrowserRouter> - useNavigate() tu nie zadziała.
  useEffect(() => {
    if (window.__cafeFetchPatched) return;
    window.__cafeFetchPatched = true;
    const origFetch = window.fetch.bind(window);
    window.fetch = async (...args) => {
      const res = await origFetch(...args);
      if (res.status === 401) {
        const url = String(args[0] || '');
        // Nie przekierowuj przy JAKIEJKOLWIEK próbie logowania (główne hasło, PIN
        // pracownika, itd.) - 401 tam to po prostu złe dane logowania, a nie
        // wygasły token istniejącej, ważnej sesji (np. terminala). Zły PIN nie
        // może wylogowywać całego terminala - ma tylko pokazać błąd na ekranie PIN.
        const isAuthAttempt = url.includes('/api/auth/');
        const alreadyOnLogin = window.location.pathname.startsWith('/login');
        if (!isAuthAttempt && !alreadyOnLogin) {
          localStorage.removeItem('cafepos_token');
          localStorage.removeItem('cafepos_user');
          window.location.href = '/login';
        }
      }
      return res;
    };
  }, []);

  const resetTimer = useCallback(() => {
    if (!terminalUser) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setTerminalUser(null);
      setWorkerToken(null);
    }, timeoutSecs * 1000);
  }, [terminalUser, timeoutSecs]);

  useEffect(() => {
    if (!terminalUser) return;
    const events = ['click', 'keydown', 'touchstart', 'mousemove'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [terminalUser, resetTimer]);

  async function login(username, password) {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Błąd logowania');
    localStorage.setItem('cafepos_token', data.token);
    // Pobierz pełny profil
    let profile = { name: data.name, role: data.role };
    try {
      const meRes = await fetch('/api/auth/me', { headers: { Authorization: `Bearer ${data.token}` } });
      const me = await meRes.json();
      profile = { ...profile, first_name: me.first_name, last_name: me.last_name };
    } catch {}
    localStorage.setItem('cafepos_user', JSON.stringify(profile));
    setUser(profile);
    if (data.role === 'terminal') setTerminalToken(data.token);
    return data;
  }

  async function loginPin(userData) {
    // userData zawiera { id, name, role, token } — token pracownika
    let profile = { id: userData.id, name: userData.name, role: userData.role };
    try {
      const meRes = await fetch('/api/auth/me', { headers: { Authorization: `Bearer ${userData.token}` } });
      const me = await meRes.json();
      profile = { ...profile, first_name: me.first_name, last_name: me.last_name };
    } catch {}
    setTerminalUser(profile);
    setWorkerToken(userData.token);
    resetTimer();
  }

  function logoutPin() {
    setTerminalUser(null);
    setWorkerToken(null);
    if (timerRef.current) clearTimeout(timerRef.current);
  }

  function logout() {
    localStorage.removeItem('cafepos_token');
    localStorage.removeItem('cafepos_user');
    setUser(null);
    setTerminalUser(null);
    setTerminalToken(null);
    setWorkerToken(null);
  }

  const isTerminal    = user?.role === 'terminal';
  const activeUser    = isTerminal ? terminalUser : user;
  const effectiveRole = activeUser?.role;
  const apiToken      = isTerminal && workerToken ? workerToken : localStorage.getItem('cafepos_token');

  // Ustaw token synchronicznie przy każdym renderze
  setApiToken(apiToken);
  if (typeof window !== 'undefined') window.__cafeApiToken = apiToken;

  return (
    <AuthContext.Provider value={{
      user: activeUser,
      rawUser: user,
      terminalUser,
      terminalToken,
      workerToken,
      apiToken,
      isTerminal,
      effectiveRole,
      loading,
      login,
      loginPin,
      logoutPin,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
